import { MessageCircle, MessageSquareText, SquarePen } from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import type { PayPeriodLockTarget } from "@/payroll/pay-periods/pay-period-lock";
import { PayBreakdownMoreActionsMenu } from "./pay-breakdown-more-actions-menu";
import {
  isPayBreakdownRerunMenuActionVisible,
  type PayBreakdownRerunMenuAction,
} from "./pay-breakdown-rerun-action";
import type {
  PayBreakdownCommentsAction,
  PayBreakdownDownloadAction,
  PayBreakdownManualEditsAction,
} from "./types";

interface PayBreakdownWorkbookHeaderActions {
  download: PayBreakdownDownloadAction;
  manualEdits?: PayBreakdownManualEditsAction;
  comments?: PayBreakdownCommentsAction;
  approvalRequest?: ReactNode;
}

interface PayBreakdownHeaderActionsProps {
  workbook?: PayBreakdownWorkbookHeaderActions;
  explanationChat?: {
    onOpen: () => void;
    placement: "button" | "menu";
  };
  rerun?: PayBreakdownRerunMenuAction;
  lock?: PayPeriodLockTarget;
  isLocked: boolean;
}

export const PayBreakdownHeaderActions = ({
  workbook,
  explanationChat,
  rerun,
  lock,
  isLocked,
}: PayBreakdownHeaderActionsProps) => {
  const hasEditsToReview = (workbook?.manualEdits?.reviewCount ?? 0) > 0;
  const showManualEditsReview = hasEditsToReview && !isLocked;
  const hasRerun = isPayBreakdownRerunMenuActionVisible(rerun);
  // Like manual edits: a header button while there is something open to read,
  // otherwise a More actions entry.
  const hasOpenComments = (workbook?.comments?.openCount ?? 0) > 0;

  if (!workbook && !explanationChat && !hasRerun && !lock) return null;

  return (
    <Stack
      direction="row"
      align="center"
      justify="end"
      gap="sm"
      className="w-full flex-wrap sm:w-auto"
    >
      {explanationChat?.placement === "button" && (
        <Button
          size="sm"
          variant="secondary"
          onClick={explanationChat.onOpen}
          leftIcon={<MessageCircle className="h-4 w-4" />}
        >
          Ask AI
        </Button>
      )}
      {workbook && (
        <>
          {workbook.approvalRequest}
          {workbook.comments && hasOpenComments && (
            <Button
              size="sm"
              variant="secondary"
              onClick={workbook.comments.onOpen}
              leftIcon={<MessageSquareText className="h-4 w-4" />}
            >
              Comments
              <Badge variant="orange" size="sm" className="ml-1.5">
                {workbook.comments.openCount}
              </Badge>
            </Button>
          )}
          {workbook.manualEdits && showManualEditsReview && (
            <Button
              size="sm"
              variant="secondary"
              onClick={workbook.manualEdits.onOpen}
              leftIcon={<SquarePen className="h-4 w-4" />}
            >
              Manual edits
              <Badge variant="gold" size="sm" className="ml-1.5">
                {workbook.manualEdits.reviewCount}
              </Badge>
            </Button>
          )}
        </>
      )}
      <PayBreakdownMoreActionsMenu
        onOpenExplanationChat={
          explanationChat?.placement === "menu"
            ? explanationChat.onOpen
            : undefined
        }
        download={workbook?.download}
        onOpenManualEdits={
          workbook?.manualEdits && (!hasEditsToReview || isLocked)
            ? workbook.manualEdits.onOpen
            : undefined
        }
        onOpenComments={
          workbook?.comments && !hasOpenComments
            ? workbook.comments.onOpen
            : undefined
        }
        rerun={rerun}
        lock={lock}
      />
    </Stack>
  );
};
